import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { BarChart3, Users, FileText, LogIn, RefreshCw, Calendar, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { getAuthHeaders } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

function UserStatItem({ u }) {
  return (
    <div className="p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
          <span className="text-blue-400 text-xs font-bold">{u.username?.charAt(0).toUpperCase() || "?"}</span>
        </div>
        <div>
          <p className="text-sm font-semibold text-white">{u.username}</p>
          <p className="text-xs text-zinc-500">{u.email}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-lg font-bold text-white">{u.total}</p>
        <p className="text-xs text-zinc-500">{u.total_discrepancies} findings</p>
      </div>
    </div>
  );
}

function UserStatsList({ users }) {
  return (
    <div className="divide-y divide-white/5">
      {users.map((u, i) => <UserStatItem key={i} u={u} />)}
    </div>
  );
}

function DailyChart({ data, maxCount }) {
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((d, i) => {
        const height = maxCount > 0 ? (d.count / maxCount) * 100 : 0;
        return (
          <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${d._id}: ${d.count}`}>
            <div 
              className="w-full bg-blue-500/60 rounded-t hover:bg-blue-500 transition-colors"
              style={{ height: `${Math.max(height, 4)}%` }}
            />
          </div>
        );
      })}
    </div>
  );
}

function SummaryCards({ summary }) {
  if (!summary) return null;
  
  return (
    <div className="grid grid-cols-4 gap-4 mb-6">
      <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-zinc-400 text-xs">Total Users</span>
            <Users className="text-blue-400" size={16} />
          </div>
          <p className="text-2xl font-bold text-white mt-1">{summary.users?.total || 0}</p>
          <p className="text-xs text-zinc-500">{summary.users?.admins || 0} admins</p>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-zinc-400 text-xs">Total Comparisons</span>
            <FileText className="text-emerald-400" size={16} />
          </div>
          <p className="text-2xl font-bold text-white mt-1">{summary.comparisons?.total || 0}</p>
          <p className="text-xs text-zinc-500">{summary.comparisons?.this_week || 0} this week</p>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-zinc-400 text-xs">Total Logins</span>
            <LogIn className="text-amber-400" size={16} />
          </div>
          <p className="text-2xl font-bold text-white mt-1">{summary.logins?.total || 0}</p>
          <p className="text-xs text-zinc-500">{summary.logins?.today || 0} today</p>
        </CardContent>
      </Card>
      <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-zinc-400 text-xs">Emails Sent</span>
            <TrendingUp className="text-purple-400" size={16} />
          </div>
          <p className="text-2xl font-bold text-white mt-1">{summary.emails?.sent || 0}</p>
          <p className="text-xs text-zinc-500">{summary.emails?.failed || 0} failed</p>
        </CardContent>
      </Card>
    </div>
  );
}

function LoginHistoryTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    axios.get(`${API}/admin/usage/logins?skip=${page * limit}&limit=${limit}`, { headers: getAuthHeaders() })
      .then((res) => { setLogs(res.data.logs); setTotal(res.data.total); })
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, [page]);

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>;

  return (
    <div>
      <Card className="bg-zinc-900/50 border-white/5">
        <CardHeader>
          <CardTitle className="text-lg text-white">Login History ({total})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/5 text-left">
                    <th className="p-4 text-xs text-zinc-500 font-medium">User</th>
                    <th className="p-4 text-xs text-zinc-500 font-medium">Email</th>
                    <th className="p-4 text-xs text-zinc-500 font-medium">IP Address</th>
                    <th className="p-4 text-xs text-zinc-500 font-medium">Date/Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-white/5">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center">
                            <span className="text-blue-400 text-xs font-bold">
                              {log.username?.charAt(0).toUpperCase() || "?"}
                            </span>
                          </div>
                          <span className="text-sm text-white">{log.username}</span>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-zinc-400">{log.user_email}</td>
                      <td className="p-4 text-sm text-zinc-500 font-mono">{log.ip_address}</td>
                      <td className="p-4 text-sm text-zinc-500">{new Date(log.login_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-zinc-500">No login history</div>
          )}
        </CardContent>
      </Card>

      {total > limit && (
        <div className="flex items-center justify-center gap-4 mt-4">
          <Button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} variant="outline" className="border-zinc-700 text-zinc-300">Previous</Button>
          <span className="text-zinc-500 text-sm">Page {page + 1} of {Math.ceil(total / limit)}</span>
          <Button onClick={() => setPage(page + 1)} disabled={(page + 1) * limit >= total} variant="outline" className="border-zinc-700 text-zinc-300">Next</Button>
        </div>
      )}
    </div>
  );
}

function ComparisonStatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/usage/comparisons`, { headers: getAuthHeaders() })
      .then((res) => setStats(res.data))
      .catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="animate-spin text-blue-500" size={24} /></div>;

  const perUser = stats?.per_user || [];
  const daily = stats?.daily || [];
  const maxCount = daily.length > 0 ? Math.max(...daily.map(x => x.count)) : 1;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-white/5">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500">Today</p>
            <p className="text-2xl font-bold text-white">{stats?.summary?.today || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/5">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500">This Week</p>
            <p className="text-2xl font-bold text-white">{stats?.summary?.this_week || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/5">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500">This Month</p>
            <p className="text-2xl font-bold text-white">{stats?.summary?.this_month || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/5">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500">Total</p>
            <p className="text-2xl font-bold text-white">{stats?.summary?.total || 0}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-zinc-900/50 border-white/5">
        <CardHeader>
          <CardTitle className="text-lg text-white">Comparisons by User</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {perUser.length > 0 ? (
            <UserStatsList users={perUser} />
          ) : (
            <div className="p-8 text-center text-zinc-500">No comparison data</div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/50 border-white/5">
        <CardHeader>
          <CardTitle className="text-lg text-white">Daily Activity (Last 30 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          {daily.length > 0 ? (
            <DailyChart data={daily} maxCount={maxCount} />
          ) : (
            <div className="h-32 flex items-center justify-center text-zinc-500">No daily data</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function UsageAnalyticsPage() {
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("comparisons");

  useEffect(() => {
    axios.get(`${API}/admin/usage/summary`, { headers: getAuthHeaders() })
      .then((res) => setSummary(res.data))
      .catch(() => {});
  }, []);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="min-h-screen p-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="text-blue-400" size={28} />
            Usage Analytics
          </h1>
          <p className="text-zinc-500 mt-1">Monitor system usage and user activity</p>
        </div>

        <SummaryCards summary={summary} />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-zinc-900 border border-white/10 mb-6">
            <TabsTrigger value="comparisons" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
              <FileText size={16} className="mr-2" /> Comparisons
            </TabsTrigger>
            <TabsTrigger value="logins" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400">
              <LogIn size={16} className="mr-2" /> Login History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="comparisons">
            <ComparisonStatsTab />
          </TabsContent>

          <TabsContent value="logins">
            <LoginHistoryTab />
          </TabsContent>
        </Tabs>
      </div>
    </motion.div>
  );
}
